import { useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  FileJson,
  Loader2,
  Palette,
  PanelBottom,
  RotateCcw,
  Sliders,
  Type,
  Upload,
  Monitor,
  Navigation,
} from 'lucide-react';
import { kreatorApi } from '../../lib/api';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import type { KreatorSettingsData } from '../KreatorPage';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

const RESET_SECTIONS = [
  { key: 'settings', label: 'Wygląd stron - Ogólne', description: 'Szerokości, odstępy, animacje i domyślne style ikon.', icon: Sliders },
  { key: 'pageAppearance', label: 'Wygląd stron', description: 'Ustawienia strony produktu, koszyka, checkoutu, listingu i podziękowania.', icon: Monitor },
  { key: 'colors', label: 'Kolory', description: 'Paleta podstawowa i dodatkowa sklepu.', icon: Palette },
  { key: 'typography', label: 'Typografia', description: 'Fonty, rozmiary, interlinie i responsywność tekstów.', icon: Type },
  { key: 'navigation', label: 'Nawigacja', description: 'Logo, pasek menu, promobar i zachowanie nagłówka.', icon: Navigation },
  { key: 'footer', label: 'Stopka', description: 'Układ, linki, kolory i domyślna treść stopki.', icon: PanelBottom },
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractImportedSettings(payload: unknown): Record<string, unknown> | null {
  if (!isRecord(payload)) return null;
  if (isRecord(payload.settings)) return payload.settings;
  if (RESET_SECTIONS.some(section => Object.prototype.hasOwnProperty.call(payload, section.key))) {
    return payload;
  }
  return null;
}

export function KreatorImportExportSection({
  integrationId,
  onSaved,
  showSuccess,
  showError,
}: Props) {
  const confirmDialog = useConfirmDialog();
  const fileRef = useRef<HTMLInputElement>(null);
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const [importFileName, setImportFileName] = useState('');

  const handleResetAll = async () => {
    const confirmed = await confirmDialog({
      title: 'Przywróć wszystkie domyślne',
      message: 'Przywrócić wszystkie ustawienia Kreatora do domyślnych wartości?',
      description: 'Ta operacja nadpisze bieżące ustawienia sekcji Wygląd stron, Kolory, Typografia, Nawigacja i Stopka.',
      confirmLabel: 'Przywróć wszystkie',
      danger: true,
    });
    if (!confirmed) return;

    setRunningAction('all');
    try {
      await kreatorApi.resetToDefaults(integrationId);
      onSaved();
      showSuccess('Przywrócono wszystkie domyślne ustawienia Kreatora');
    } catch {
      showError('Nie udało się przywrócić domyślnych ustawień');
    } finally {
      setRunningAction(null);
    }
  };

  const handleResetSection = async (section: typeof RESET_SECTIONS[number]) => {
    const confirmed = await confirmDialog({
      title: `Przywróć sekcję: ${section.label}`,
      message: `Przywrócić domyślne ustawienia tylko dla sekcji "${section.label}"?`,
      confirmLabel: 'Przywróć sekcję',
      danger: true,
    });
    if (!confirmed) return;

    setRunningAction(section.key);
    try {
      await kreatorApi.resetSectionToDefaults(integrationId, section.key);
      onSaved();
      showSuccess(`Przywrócono domyślne ustawienia sekcji "${section.label}"`);
    } catch {
      showError(`Nie udało się przywrócić sekcji "${section.label}"`);
    } finally {
      setRunningAction(null);
    }
  };

  const handleExport = async () => {
    setRunningAction('export');
    try {
      const res = await kreatorApi.exportSettings(integrationId);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `ecomflow-kreator-settings-${date}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccess('Wyeksportowano ustawienia Kreatora');
    } catch {
      showError('Nie udało się wyeksportować ustawień');
    } finally {
      setRunningAction(null);
    }
  };

  const handleImportFile = async (file: File) => {
    setImportFileName(file.name);
    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw);
      const importedSettings = extractImportedSettings(parsed);
      if (!importedSettings) {
        showError('Plik nie zawiera obsługiwanych ustawień Kreatora');
        return;
      }

      const confirmed = await confirmDialog({
        title: 'Import ustawień',
        message: `Zaimportować ustawienia z pliku "${file.name}"?`,
        description: 'Import nadpisze rozpoznane sekcje ustawień Kreatora. Dane produktowe nie są importowane.',
        confirmLabel: 'Importuj',
        danger: true,
      });
      if (!confirmed) return;

      setRunningAction('import');
      const res = await kreatorApi.importSettings(integrationId, importedSettings);
      const importedSections = (res.data as { importedSections?: string[] })?.importedSections ?? [];
      onSaved();
      showSuccess(importedSections.length > 0
        ? `Zaimportowano sekcje: ${importedSections.join(', ')}`
        : 'Zaimportowano ustawienia Kreatora');
    } catch {
      showError('Nie udało się zaimportować pliku JSON');
    } finally {
      setRunningAction(null);
      setImportFileName('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="p-3 rounded-xl bg-danger/10 border border-danger/20 w-fit">
            <RotateCcw className="w-5 h-5 text-danger" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-text-primary">Przywróć domyślne</h2>
            <p className="text-sm text-text-secondary mt-1">
              Reset całości przywraca domyślne ustawienia Kreatora. Uważaj! Zmiany są nieodwracalne!
            </p>
          </div>
          <button
            type="button"
            onClick={handleResetAll}
            disabled={runningAction !== null}
            className="btn-primary text-sm flex items-center gap-2 lg:ml-auto disabled:opacity-60"
          >
            {runningAction === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Przywróć wszystkie
          </button>
        </div>
      </div>

      <div className="card space-y-3">
        <div>
          <h2 className="text-base font-semibold text-text-primary">Reset pojedynczych sekcji</h2>
          <p className="text-sm text-text-secondary mt-1">
            Użyj, gdy chcesz cofnąć tylko konkretną część konfiguracji.
          </p>
        </div>

        <div className="divide-y divide-border-subtle rounded-xl border border-border-subtle overflow-hidden">
          {RESET_SECTIONS.map(section => {
            const Icon = section.icon;
            return (
              <div key={section.key} className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-surface-raised/40">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded-lg bg-accent-primary/10 border border-accent-primary/20 shrink-0">
                    <Icon className="w-4 h-4 text-accent-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-text-primary">{section.label}</p>
                    <p className="text-xs text-text-secondary mt-0.5">{section.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleResetSection(section)}
                  disabled={runningAction !== null}
                  className="btn-secondary text-xs flex items-center justify-center gap-1.5 md:w-40 disabled:opacity-60"
                >
                  {runningAction === section.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Resetuj
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="flex items-start gap-3 mb-5">
          <div className="p-3 rounded-xl bg-accent-primary/10 border border-accent-primary/20">
            <FileJson className="w-5 h-5 text-accent-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text-primary">Import i eksport ustawień</h2>
            <p className="text-sm text-text-secondary mt-1">
              Eksportuje konfigurację Kreatora do pliku JSON. Import nadpisuje rozpoznane sekcje ustawień.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border-subtle bg-surface-raised/40 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Download className="w-4 h-4 text-accent-primary mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-text-primary">Eksport ustawień</p>
                <p className="text-xs text-text-secondary mt-0.5">Pobierz aktualną konfigurację do pliku JSON.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleExport}
              disabled={runningAction !== null}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {runningAction === 'export' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Eksportuj JSON
            </button>
          </div>

          <div className="rounded-xl border border-border-subtle bg-surface-raised/40 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Upload className="w-4 h-4 text-accent-primary mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-text-primary">Import ustawień</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Wgraj plik JSON wyeksportowany z Kreatora.
                </p>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleImportFile(file);
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={runningAction !== null}
              className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {runningAction === 'import' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Importuj JSON
            </button>
            {importFileName && (
              <p className="flex items-center gap-1.5 text-xs text-text-muted">
                <AlertCircle className="w-3.5 h-3.5" />
                {importFileName}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KreatorImportExportSection;
